import { Node, Extension, mergeAttributes, type Command } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewContent, NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { ChevronRight, ChevronDown, Trash2 } from 'lucide-react';
import { findParentNode } from '@tiptap/core';
import { DrawingCanvas } from '@/components/drawing-canvas';
import { useState } from 'react';

// Word-like Tab navigation for tables: Tab moves to the next cell, and when
// pressed in the very last cell of the table it inserts a new row and moves
// into it (matching Microsoft Word's table behavior). Shift-Tab moves back.
export const TableKeymap = Extension.create({
  name: 'tableKeymap',

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        const { editor } = this;
        if (!editor.isActive('table')) {
          return false;
        }
        if (editor.commands.goToNextCell()) {
          return true;
        }
        return editor.chain().addRowAfter().goToNextCell().run();
      },
      'Shift-Tab': () => {
        const { editor } = this;
        if (!editor.isActive('table')) {
          return false;
        }
        return editor.commands.goToPreviousCell();
      },
    };
  },
});

// FontSize Extension for Tiptap
export const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize?.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize: (fontSize: string) => ({ chain }: any) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run();
      },
      unsetFontSize: () => ({ chain }: any) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    };
  },
});

// Module augmentation to add our custom commands to Tiptap's Commands interface
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    collapsibleSection: {
      insertCollapsible: () => ReturnType;
      toggleCollapsible: () => ReturnType;
      setCollapsibleOpen: (open: boolean) => ReturnType;
      expandAllCollapsibles: () => ReturnType;
      collapseAllCollapsibles: () => ReturnType;
    };
    drawingNode: {
      insertDrawing: () => ReturnType;
    };
  }
}

// React component for the collapsible section
function CollapsibleSectionView({ node, updateAttributes, editor, getPos, deleteNode }: NodeViewProps) {
  const isOpen = node.attrs.open;
  const title = node.attrs.title || '';
  
  const toggleOpen = () => {
    updateAttributes({ open: !isOpen });
  };
  
  const updateTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateAttributes({ title: e.target.value });
  };
  
  return (
    <NodeViewWrapper className="collapsible-section my-4">
      <div className="collapsible-header-wrapper flex items-start gap-2 mb-2">
        <button
          type="button"
          className="collapsible-toggle mt-1 p-0 border-0 bg-transparent cursor-pointer text-red-500 hover:text-red-700 transition-colors"
          onClick={toggleOpen}
          contentEditable={false}
          data-testid="collapsible-toggle"
          aria-label={isOpen ? "Collapse section" : "Expand section"}
        >
          {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
        <input
          type="text"
          className="collapsible-title-input flex-1 font-semibold text-lg border-0 border-b-2 border-transparent focus:border-primary focus:outline-none bg-transparent px-2 py-1"
          value={title}
          onChange={updateTitle}
          contentEditable={false}
          data-testid="collapsible-title-input"
          placeholder="Section title..."
        />
        <button
          type="button"
          className="collapsible-delete mt-1 p-0 border-0 bg-transparent cursor-pointer text-muted-foreground hover:text-destructive transition-colors"
          onClick={deleteNode}
          contentEditable={false}
          data-testid="collapsible-delete"
          aria-label="Delete section"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div 
        className="collapsible-body pl-7" 
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        <NodeViewContent className="collapsible-content prose prose-sm max-w-none" />
      </div>
    </NodeViewWrapper>
  );
}

// Main collapsible section node
export const CollapsibleSection = Node.create({
  name: 'collapsibleSection',
  group: 'block',
  content: 'block+',
  defining: true,
  isolating: true,
  
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => {
          if (!attributes.id) return {};
          return { 'data-id': attributes.id };
        },
      },
      open: {
        default: true,
        parseHTML: element => element.getAttribute('data-open') === 'true',
        renderHTML: attributes => {
          return { 'data-open': String(attributes.open) };
        },
      },
      title: {
        default: '',
        parseHTML: element => element.getAttribute('data-title') || '',
        renderHTML: attributes => {
          return { 'data-title': attributes.title };
        },
      },
    };
  },
  
  parseHTML() {
    return [{
      tag: 'div[data-type="collapsibleSection"]',
      getAttrs: (dom) => {
        if (typeof dom === 'string') return {};
        return {
          id: dom.getAttribute('data-id'),
          open: dom.getAttribute('data-open') === 'true',
          title: dom.getAttribute('data-title') || '',
        };
      },
    }];
  },
  
  renderHTML({ node, HTMLAttributes }) {
    return [
      'div', 
      mergeAttributes(HTMLAttributes, { 
        'data-type': 'collapsibleSection',
        'data-id': node.attrs.id,
        'data-open': String(node.attrs.open),
        'data-title': node.attrs.title,
        class: 'collapsible-section',
      }),
      0, // This ensures nested content is preserved
    ];
  },
  
  addNodeView() {
    return ReactNodeViewRenderer(CollapsibleSectionView);
  },
  
  addCommands() {
    return {
      insertCollapsible: () => ({ state, chain, commands, tr, dispatch }: any) => {
        // Check if we're inside a collapsible section
        const collapsiblePredicate = (node: any) => node.type.name === 'collapsibleSection';
        let parentCollapsible = findParentNode(collapsiblePredicate)(state.selection);
        
        // If we're inside a collapsible section, we need to move outside first
        if (parentCollapsible) {
          // Find the outermost collapsible section (in case of nesting)
          let outermostPos = parentCollapsible.pos;
          let outermostNode = parentCollapsible.node;
          
          // Walk up to find the outermost collapsible
          const { doc } = state;
          doc.nodesBetween(0, parentCollapsible.pos, (node: any, pos: number) => {
            if (node.type.name === 'collapsibleSection' && pos < parentCollapsible!.pos) {
              // Check if this collapsible contains our current one
              const endPos = pos + node.nodeSize;
              if (endPos >= parentCollapsible!.pos + parentCollapsible!.node.nodeSize) {
                outermostPos = pos;
                outermostNode = node;
              }
            }
          });
          
          // Calculate position after the outermost collapsible section
          const afterPos = outermostPos + outermostNode.nodeSize;
          
          // Move cursor to after the outermost collapsible section and insert
          return chain()
            .focus()
            .command(({ tr: transaction }: any) => {
              // Set selection to after the collapsible section
              transaction.setSelection(
                state.selection.constructor.near(transaction.doc.resolve(afterPos))
              );
              return true;
            })
            .insertContent({
              type: this.name,
              attrs: { 
                open: true, 
                id: `collapsible-${Date.now()}`,
                title: ''
              },
              content: [
                {
                  type: 'paragraph',
                  content: [],
                },
              ],
            })
            .run();
        }
        
        // Not inside a collapsible, just insert normally
        return commands.insertContent({
          type: this.name,
          attrs: { 
            open: true, 
            id: `collapsible-${Date.now()}`,
            title: ''
          },
          content: [
            {
              type: 'paragraph',
              content: [],
            },
          ],
        });
      },
      toggleCollapsible: () => ({ state, chain }: any) => {
        // Find parent collapsible section from current selection
        const collapsiblePredicate = (node: any) => node.type.name === this.name;
        const parentNode = findParentNode(collapsiblePredicate)(state.selection);
        
        if (parentNode) {
          const { node, pos } = parentNode;
          // Use setNodeMarkup to update attributes at the specific position
          return chain()
            .command(({ tr }: any) => {
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                open: !node.attrs.open,
              });
              return true;
            })
            .run();
        }
        return false;
      },
      setCollapsibleOpen: (open: boolean) => ({ commands }: any) => {
        return commands.updateAttributes(this.name, { open });
      },
      expandAllCollapsibles: () => ({ tr, state, dispatch }: any) => {
        const { doc } = state;
        let changed = false;
        
        doc.descendants((node: any, pos: number) => {
          if (node.type.name === 'collapsibleSection' && !node.attrs.open) {
            if (dispatch) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, open: true });
              changed = true;
            }
          }
        });
        
        if (changed && dispatch) {
          dispatch(tr);
        }
        return changed;
      },
      collapseAllCollapsibles: () => ({ tr, state, dispatch }: any) => {
        const { doc } = state;
        let changed = false;
        
        doc.descendants((node: any, pos: number) => {
          if (node.type.name === 'collapsibleSection' && node.attrs.open) {
            if (dispatch) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, open: false });
              changed = true;
            }
          }
        });
        
        if (changed && dispatch) {
          dispatch(tr);
        }
        return changed;
      },
    };
  },
});

// React component for the drawing node
function DrawingNodeView({ node, updateAttributes, deleteNode }: NodeViewProps) {
  // Initialize editing state from node attributes to survive Tiptap remounts
  // This is critical for fullscreen mode where Radix dropdowns move focus outside NodeView
  const [isEditing, setIsEditingLocal] = useState(() => {
    // Check if we should be in edit mode from persisted attribute
    if (node.attrs.isEditing) return true;
    // Default: edit mode if no data yet
    return !node.attrs.drawingData;
  });
  
  // Also track fullscreen state in node attributes to survive remounts
  const isFullscreen = node.attrs.isFullscreen || false;
  
  const drawingData = node.attrs.drawingData;
  const canvasHeight = node.attrs.canvasHeight || 400;

  // Sync editing state with node attributes to persist across remounts
  const setIsEditing = (editing: boolean) => {
    setIsEditingLocal(editing);
    updateAttributes({ isEditing: editing });
  };
  
  // Sync fullscreen state with node attributes
  const handleFullscreenChange = (fullscreen: boolean) => {
    updateAttributes({ isFullscreen: fullscreen });
  };

  const handleSave = (data: string, height: number) => {
    updateAttributes({ drawingData: data, canvasHeight: height, isEditing: false, isFullscreen: false });
    setIsEditingLocal(false);
  };

  const handleCancel = () => {
    if (!drawingData) {
      deleteNode();
    } else {
      updateAttributes({ isEditing: false, isFullscreen: false });
      setIsEditingLocal(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleHeightChange = (height: number) => {
    updateAttributes({ canvasHeight: height });
  };

  const handleAutoSave = (data: string) => {
    updateAttributes({ drawingData: data });
  };

  return (
    <NodeViewWrapper className="drawing-node my-4" data-testid="drawing-node">
      {isEditing ? (
        <DrawingCanvas
          initialData={drawingData}
          onSave={handleSave}
          onCancel={handleCancel}
          height={canvasHeight}
          onHeightChange={handleHeightChange}
          isFullscreen={isFullscreen}
          onFullscreenChange={handleFullscreenChange}
          onAutoSave={handleAutoSave}
        />
      ) : (
        <div 
          className="drawing-preview cursor-pointer border rounded-lg overflow-hidden hover:border-primary transition-colors bg-white dark:bg-gray-900"
          onClick={handleEdit}
          data-testid="drawing-preview"
        >
          {drawingData ? (
            <DrawingCanvas
              initialData={drawingData}
              height={canvasHeight}
              readOnly={true}
            />
          ) : (
            <div className="text-sm text-muted-foreground text-center p-4">
              Click to start drawing
            </div>
          )}
        </div>
      )}
    </NodeViewWrapper>
  );
}

// Drawing node extension
export const DrawingNode = Node.create({
  name: 'drawingNode',
  group: 'block',
  atom: true,
  
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => {
          if (!attributes.id) return {};
          return { 'data-id': attributes.id };
        },
      },
      drawingData: {
        default: null,
        parseHTML: element => element.getAttribute('data-drawing'),
        renderHTML: attributes => {
          if (!attributes.drawingData) return {};
          return { 'data-drawing': attributes.drawingData };
        },
      },
      canvasHeight: {
        default: 400,
        parseHTML: element => {
          const height = element.getAttribute('data-canvas-height');
          return height ? parseInt(height, 10) : 400;
        },
        renderHTML: attributes => {
          return { 'data-canvas-height': String(attributes.canvasHeight || 400) };
        },
      },
      isEditing: {
        default: false,
        parseHTML: element => element.getAttribute('data-is-editing') === 'true',
        renderHTML: attributes => {
          return { 'data-is-editing': String(!!attributes.isEditing) };
        },
      },
      isFullscreen: {
        default: false,
        parseHTML: element => element.getAttribute('data-is-fullscreen') === 'true',
        renderHTML: attributes => {
          return { 'data-is-fullscreen': String(!!attributes.isFullscreen) };
        },
      },
    };
  },
  
  parseHTML() {
    return [{
      tag: 'div[data-type="drawingNode"]',
      getAttrs: (dom) => {
        if (typeof dom === 'string') return {};
        const heightAttr = dom.getAttribute('data-canvas-height');
        return {
          id: dom.getAttribute('data-id'),
          drawingData: dom.getAttribute('data-drawing'),
          canvasHeight: heightAttr ? parseInt(heightAttr, 10) : 400,
          isEditing: dom.getAttribute('data-is-editing') === 'true',
          isFullscreen: dom.getAttribute('data-is-fullscreen') === 'true',
        };
      },
    }];
  },
  
  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'drawingNode',
        'data-id': node.attrs.id,
        'data-drawing': node.attrs.drawingData || '',
        'data-canvas-height': String(node.attrs.canvasHeight || 400),
        'data-is-editing': String(!!node.attrs.isEditing),
        'data-is-fullscreen': String(!!node.attrs.isFullscreen),
        class: 'drawing-node',
      }),
      'Drawing',
    ];
  },
  
  addNodeView() {
    return ReactNodeViewRenderer(DrawingNodeView);
  },
  
  addCommands() {
    return {
      insertDrawing: () => ({ commands }: any) => {
        return commands.insertContent({
          type: this.name,
          attrs: {
            id: `drawing-${Date.now()}`,
            drawingData: null,
          },
        });
      },
    };
  },
});
